import { Flex, Text, Button, Box, Tooltip } from '@radix-ui/themes';
import { useTranslation } from 'react-i18next';
import {
  DOT_COLORS,
  STATUS_TYPES,
  formatDate,
  getEffectiveDisplayStatus,
  getEffectiveDisplayDate,
} from './helpers.js';
import RowAction from '../RowAction.jsx';
import styles from './CalendarView.module.css';

export default function CampaignItem({
  campaign,
  userType,
  hoveredCampaign,
  onHover,
  onClick,
  onDateClick,
  onArchiveModalChange,
  isModalOrDropdownClick,
  monthIndex,
  year,
}) {
  const { t } = useTranslation('campaigns');

  const status = campaign.status || STATUS_TYPES.DRAFT;
  const displayStatus = getEffectiveDisplayStatus(campaign, monthIndex, year);
  const displayDate = getEffectiveDisplayDate(campaign, monthIndex, year);
  const isTruncated = campaign.name.length > 35;
  const truncatedName =
    campaign.name.length > 35
      ? `${campaign.name.slice(0, 32)}…`
      : campaign.name;
  const ariaLabel = `${campaign.name} (${t(
    `calendar.${displayStatus}`,
  )} status)`;
  const formattedDate = formatDate(displayDate);
  const hasPosts = !!campaign.earliestPostDate;

  // Note: "completed" status is treated as "active" (live)
  const isDateEditDisabled =
    status === STATUS_TYPES.ACTIVE ||
    status === STATUS_TYPES.ARCHIVED ||
    status === STATUS_TYPES.COMPLETED ||
    (status === STATUS_TYPES.DRAFT && hasPosts);

  const getTooltipText = () => {
    if (status === STATUS_TYPES.ACTIVE || status === STATUS_TYPES.COMPLETED) {
      return t('calendar.activeCampaignsCannotBeEdited');
    }
    if (status === STATUS_TYPES.ARCHIVED) {
      return t('calendar.archivedCampaignsCannotBeEdited');
    }
    if (status === STATUS_TYPES.DRAFT && hasPosts) {
      return t('calendar.draftDateDeterminedByPosts');
    }
    return t('calendar.editDate');
  };

  return (
    <Flex
      className={styles.campaignItem}
      style={{ borderLeft: `2px solid ${DOT_COLORS[displayStatus]}` }}
      align="center"
      justify="between"
      as="li"
      tabIndex={0}
      aria-label={ariaLabel}
      onMouseEnter={() => onHover(campaign.uuid, true)}
      onMouseLeave={() => onHover(campaign.uuid, false)}
      onClick={e => {
        if (isModalOrDropdownClick(e)) {
          e.stopPropagation();
          return;
        }
        onClick(campaign);
      }}
    >
      <Flex className={styles.campaignContentContainer} align="center">
        <Tooltip
          content={`${t(`calendar.${displayStatus}`)}: ${t(
            'calendar.highlight',
          )}`}
        >
          <Flex align="center" gap="2">
            <span
              className={styles.campaignDot}
              style={{ background: DOT_COLORS[displayStatus] }}
              aria-label={`${t(`calendar.${displayStatus}`)} status`}
            />
            <i className={`fa-regular fa-star ${styles.broadcastIcon}`} />
          </Flex>
        </Tooltip>
        {isTruncated ? (
          <Tooltip content={campaign.name}>
            <Text size="1" className={styles.campaignNameText}>
              {truncatedName}
            </Text>
          </Tooltip>
        ) : (
          <Text size="1" className={styles.campaignNameText}>
            {campaign.name}
          </Text>
        )}
      </Flex>
      <Tooltip content={getTooltipText()}>
        <Button
          variant="soft"
          size="1"
          className={`${styles.dateButton} ${
            hoveredCampaign === campaign.uuid ? styles.hovered : ''
          }`}
          onClick={event => {
            if (!isDateEditDisabled) {
              onDateClick(event, campaign);
            }
          }}
          disabled={isDateEditDisabled}
        >
          {formattedDate}
        </Button>
      </Tooltip>
      <Tooltip content={t('calendar.moreOptions')}>
        <Box className={styles.rowActionContainer}>
          <RowAction
            filter={
              campaign.status === STATUS_TYPES.ARCHIVED ? 'archived' : 'active'
            }
            userType={userType}
            uuid={campaign.uuid}
            name={campaign.name}
            isArchived={campaign.status === STATUS_TYPES.ARCHIVED}
            isMultiMonth={campaign.multiMonth}
            onArchiveModalChange={onArchiveModalChange}
          />
        </Box>
      </Tooltip>
    </Flex>
  );
}
